import type { ChangeType, ScenarioChange } from "../types/scenarios";

/**
 * Normalize a raw builder adjustment into a ScenarioChange shape
 * so that toCreateChangeRequest can serialize it for the API.
 *
 * Each form emits its own ad-hoc object.  We map those into the
 * canonical { id, type, name, description, amount, date, details } shape.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeAdjustment(adj: any): ScenarioChange {
  const id = String(adj.id ?? Date.now());
  const now = new Date();

  switch (adj.type) {
    // -- Revenue: Adjust by % -------------------------------------------
    case "adjust-revenue-percent": {
      return {
        id,
        type: "change-revenue" as ChangeType,
        name: `Adjust revenue ${adj.adjustmentPercent >= 0 ? "+" : ""}${adj.adjustmentPercent}%`,
        description: adj.reason || "",
        amount: adj.adjustmentAmount ?? 0,
        date: adj.effectiveDate instanceof Date ? adj.effectiveDate : now,
        effectiveDate:
          adj.effectiveDate instanceof Date ? adj.effectiveDate : undefined,
        endDate: adj.endDate instanceof Date ? adj.endDate : undefined,
        details: {
          monthlyAmount: adj.monthlyImpact ?? 0,
          percentage: adj.adjustmentPercent,
          frequency: adj.frequency ?? "monthly",
          monthlyDateMode: adj.monthlyDateMode ?? "same-date",
          entity: adj.entity,
          confidence: adj.confidence,
        },
        createdAt: now,
        createdBy: "",
      };
    }
    // -- Cost: Adjust by % ----------------------------------------------
    case "adjust-costs-percent": {
      const isReduction = adj.adjustmentPercent < 0;
      return {
        id,
        type: "change-costs" as ChangeType,
        name: `Adjust ${adj.category || "costs"} ${adj.adjustmentPercent >= 0 ? "+" : ""}${adj.adjustmentPercent}%`,
        description: adj.reason || "",
        // Signed window total (negative = a saving) — the engine applies this
        // full amount across the adjustment window, smoothed only by frequency.
        amount: adj.adjustmentAmount ?? 0,
        date: adj.effectiveDate instanceof Date ? adj.effectiveDate : now,
        effectiveDate:
          adj.effectiveDate instanceof Date ? adj.effectiveDate : undefined,
        endDate: adj.endDate instanceof Date ? adj.endDate : undefined,
        details: {
          ...(isReduction
            ? { monthlySavings: Math.abs(adj.monthlyImpact ?? 0) }
            : { monthlyIncrease: Math.abs(adj.monthlyImpact ?? 0) }),
          changeType: isReduction ? "reduction" : "increase",
          percentage: adj.adjustmentPercent,
          frequency: adj.frequency ?? "monthly",
          monthlyDateMode: adj.monthlyDateMode ?? "same-date",
          category: adj.category,
          entity: adj.entity,
          confidence: adj.confidence,
        },
        createdAt: now,
        createdBy: "",
      };
    }
    // -- Revenue / Cost: Adjust specific transaction ---------------------
    case "adjust-transaction": {
      const isRevenue = !!adj.customer; // revenue forms set customer, cost forms set supplier
      return {
        id,
        type: (isRevenue ? "adjust-revenue" : "adjust-cost") as ChangeType,
        name: `Adjust ${adj.customer || adj.supplier || "transaction"}`,
        description: adj.reason || "",
        amount: adj.adjustedValue ?? adj.originalAmount ?? 0,
        date:
          adj.newDueDate instanceof Date
            ? adj.newDueDate
            : adj.originalDueDate
              ? new Date(adj.originalDueDate)
              : now,
        originalDueDate: adj.originalDueDate
          ? new Date(adj.originalDueDate)
          : undefined,
        newDueDate: adj.newDueDate instanceof Date ? adj.newDueDate : undefined,
        delayDays: adj.dateChangeDays ?? undefined,
        supplier: adj.supplier ?? undefined,
        details: {
          transactionId: adj.transactionId,
          valueChange: adj.valueChange,
          confidence: adj.confidence,
        },
        createdAt: now,
        createdBy: "",
      };
    }
    // -- Revenue / Cost: Add scenario transaction ------------------------
    case "add-scenario-transaction": {
      // Revenue form has `date`; cost form has `expectedDate`
      const txDate =
        adj.date instanceof Date
          ? adj.date
          : adj.expectedDate instanceof Date
            ? adj.expectedDate
            : now;
      // If amount is present and there's no customer/supplier context, decide
      // based on whether this came from cost (has businessContext) or revenue.
      const isCost = adj.businessContext !== undefined;
      return {
        id,
        type: (isCost ? "add-cost" : "add-revenue") as ChangeType,
        name: adj.description || adj.transactionType || "New transaction",
        description: adj.reason || adj.businessContext || "",
        amount: adj.amount ?? 0,
        date: txDate,
        endDate: adj.endDate instanceof Date ? adj.endDate : undefined,
        details: {
          monthlyAmount: adj.amount ?? 0,
          transactionType: adj.transactionType,
          confidence: adj.confidence,
          frequency: adj.recurrence ?? "one-off",
          monthlyDateMode: adj.monthlyDateMode ?? "same-date",
        },
        createdAt: now,
        createdBy: "",
      };
    }
    // Canonical engine types — already normalized, pass through as-is
    case "change-revenue":
    case "add-revenue":
    case "adjust-revenue":
    case "change-costs":
    case "add-cost":
    case "adjust-cost":
    case "win-customer":
    case "lose-customer":
    case "capital-purchase":
    case "hire-employees":
    case "delay-payment":
    case "accelerate-payment": {
      return {
        id,
        type: adj.type as ChangeType,
        name: adj.name || "Unnamed change",
        description: adj.description || "",
        amount: adj.amount ?? 0,
        date:
          adj.date instanceof Date
            ? adj.date
            : adj.effectiveDate instanceof Date
              ? adj.effectiveDate
              : now,
        effectiveDate:
          adj.effectiveDate instanceof Date ? adj.effectiveDate : undefined,
        endDate: adj.endDate instanceof Date ? adj.endDate : undefined,
        originalDueDate:
          adj.originalDueDate instanceof Date ? adj.originalDueDate : undefined,
        newDueDate: adj.newDueDate instanceof Date ? adj.newDueDate : undefined,
        delayDays: adj.delayDays,
        supplier: adj.supplier,
        details: adj.details ?? {},
        createdAt: adj.createdAt ?? now,
        createdBy: adj.createdBy ?? "",
      };
    }
    default: {
      // Fallback: pass through as-is, filling in required fields with defaults
      return {
        id,
        type: (adj.type || "custom") as ChangeType,
        name: adj.name || adj.description || adj.type || "Unnamed change",
        description: adj.description || adj.reason || "",
        amount: adj.amount ?? adj.adjustmentAmount ?? 0,
        date:
          adj.date instanceof Date
            ? adj.date
            : adj.effectiveDate instanceof Date
              ? adj.effectiveDate
              : now,
        details: { ...adj },
        createdAt: now,
        createdBy: "",
      };
    }
  }
}
